import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { detectInAppBrowser } from "@/utils/in-app-browser";

export function GoogleSignInButton({ prominent = false }: { prominent?: boolean }) {
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    // WhatsApp / Instagram / FB webviews block Google OAuth (disallowed_useragent).
    // Show actionable guidance instead of sending the user into a silent failure.
    const inApp = detectInAppBrowser();
    if (inApp.isInApp) {
      toast.error(`Google sign-in is blocked inside ${inApp.app}`, {
        description: "Tap the menu (⋮ or share icon) and choose 'Open in browser', then try again.",
        duration: 8000,
      });
      return;
    }

    setIsLoading(true);
    try {
      // Persist redirect info for post-OAuth callback.
      // Prefer the explicit ?redirect= query param, but don't clobber a value
      // already set in sessionStorage (e.g. by the claim flow).
      const urlParams = new URLSearchParams(window.location.search);
      const redirect = urlParams.get("redirect");
      if (redirect) {
        try {
          const decoded = decodeURIComponent(redirect);
          const url = new URL(decoded, window.location.origin);
          const path = url.pathname + url.search + url.hash;
          if (path.startsWith("/") && !path.startsWith("//")) {
            sessionStorage.setItem("postAuthRedirect", path);
          }
        } catch {
          sessionStorage.setItem("postAuthRedirect", redirect);
        }
      }


      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.origin + "/auth/callback",
        },
      });
      if (error) throw error;
    } catch (error: any) {
      console.error("Google sign-in error:", error);
      toast.error("Google sign-in failed", {
        description: error.message || "An error occurred. Please try again.",
      });
      setIsLoading(false);
    }
  };

  const googleIcon = (
    <svg viewBox="0 0 24 24" className={prominent ? "h-5 w-5" : "h-5 w-5"} aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );

  if (prominent) {
    return (
      <Button
        type="button"
        variant="outline"
        className="w-full gap-3 h-12 text-base font-medium border-2 border-primary/30 hover:border-primary/50 hover:bg-primary/5"
        onClick={handleGoogleSignIn}
        disabled={isLoading}
      >
        {googleIcon}
        {isLoading ? "Signing in..." : "Continue with Google"}
      </Button>
    );
  }

  return (
    <div className="mt-4">
      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">
            Or continue with
          </span>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        className="w-full gap-3"
        onClick={handleGoogleSignIn}
        disabled={isLoading}
      >
        {googleIcon}
        {isLoading ? "Signing in..." : "Continue with Google"}
      </Button>
    </div>
  );
}
